import {
  ChangeDetectorRef,
  ElementRef,
  OnDestroy,
  Pipe,
  PipeTransform,
  inject
} from '@angular/core';

/**
 * Schneidet zu langen Text ab und punktet das Ende dabei aus.
 *
 * Diese Pipe ist impure, da sie nicht nur abhängig von ihren direkten Eingabeparametern ist,
 * sondern auch von der Breite des Eltern-Containers.
 */
@Pipe({
  name: 'truncate',
  pure: false
})
export class TruncatePipe implements PipeTransform, OnDestroy {
  private readonly ref = inject(ElementRef);
  private readonly changeDetection = inject(ChangeDetectorRef);

  private readonly resizeObserver: ResizeObserver;
  private readonly intersectionObserver: IntersectionObserver;
  private static readonly canvasRenderingContext2D = document
    .createElement('canvas')
    .getContext('2d');

  constructor() {
    this.resizeObserver = new ResizeObserver(() => {
      // Falls sich die Größe des Target-Elements verändert, müssen wir neu berechnen.
      this.changeDetection.detectChanges();
    });

    this.resizeObserver.observe(this.ref.nativeElement);

    this.intersectionObserver = new IntersectionObserver(() => {
      // Falls sich das Element außerhalb des Viewports nicht gerendert wird,
      // z. B. in einem Slide-In, wollen wir erkennen, wenn es eingeblendet wird.
      this.changeDetection.detectChanges();
    });

    this.intersectionObserver.observe(this.ref.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    this.intersectionObserver.disconnect();
  }

  /**
   * Schneidet zu langen Text ab und punktet das Ende dabei aus.
   * @param text Text, der ggf. gekürzt werden soll.
   * @param numberOfLines Anzahl der Linien, die maximal angezeigt werden soll.
   * @param endCharacter Schlusszeichen, welches zur Auspunktung verwendet wird.
   * @param showTitel Toggle, um das Titel-Attribut hinzuzufügen, wenn ausgepunktet wurde.
   * @param minWidth Falls die Breite des Elements nicht bestimmt werden kann, wird dieser Wert als Fallback in Pixeln genommen
   */
  transform(
    text: string,
    numberOfLines: number = 3,
    endCharacter: string = '…',
    showTitel: boolean = false,
    minWidth: number = 250
  ): string {
    const parentWidth = this.calculateParentWidth(minWidth);
    const fontSize = getComputedStyle(this.ref.nativeElement).fontSize;

    return TruncatePipe.truncateWithEllipses(
      text,
      numberOfLines,
      endCharacter,
      parentWidth,
      fontSize,
      showTitel
    );
  }

  private calculateParentWidth(minWidth: number) {
    const parentElement = this.ref.nativeElement.parentElement;
    const computedStyleParent = getComputedStyle(parentElement);
    const parentPadding: number =
      parseFloat(computedStyleParent.paddingLeft ?? '0') +
      parseFloat(computedStyleParent.paddingRight ?? '0');
    return (parentElement.clientWidth || minWidth) - parentPadding;
  }

  static truncateWithEllipses(
    text: string,
    numberOfLines: number,
    endCharacter: string,
    parentWidth: number,
    fontSize: string,
    showTitel: boolean = false
  ): string {
    const { isTruncated, truncatedText } = this.addAllFittingWords(
      text,
      numberOfLines,
      endCharacter,
      parentWidth,
      fontSize
    );
    const textWithFinalCharacter = isTruncated
      ? TruncatePipe.addFinalCharacter(truncatedText, endCharacter)
      : truncatedText;
    return showTitel && isTruncated
      ? `<span aria-hidden="true" title="${text}">${textWithFinalCharacter}</span><span class="sr-only">${text}</span>`
      : textWithFinalCharacter;
  }

  /**
   * Misst die Länge eines gerenderten Strings in Pixeln
   */
  private static getTextWidth(text: string, fontSize: string): number {
    this.canvasRenderingContext2D!.font = fontSize + ' Nunito';
    const metrics = this.canvasRenderingContext2D!.measureText(text);
    return metrics.width;
  }

  /**
   * Fügt die Auspunktung an den Text an.
   */
  private static addFinalCharacter(text: string, endCharacter: string): string {
    // Entferne Satzendzeichen, die Vierfachpunkte verursachen würde
    if (text.endsWith('.') || text.endsWith('!') || text.endsWith('?')) {
      text = text.substring(0, text.length - 1);
    }

    return text + endCharacter;
  }

  /**
   * Füllt die Zeilen wortweise auf, sodass sie die Breite des Containers nicht überschreiten.
   */
  private static addAllFittingWords(
    text: string,
    numberOfLines: number,
    endCharacter: string,
    parentWidth: number,
    fontSize: string
  ): { truncatedText: string; isTruncated: boolean } {
    let truncatedText = '';

    const words = text.split(' ');
    let wordIndex = 0;

    for (let line = 0; line < numberOfLines; line++) {
      let truncatedLine = '';

      const lineStillFits = () => {
        let candidate = truncatedLine + ' ' + words[wordIndex];

        const isLastLine = line + 1 === numberOfLines;
        const isLastWord = wordIndex + 1 === words.length;
        // In der letzte Zeile muss noch das Schlusszeichen angefügt werden.
        // Ausnahme, das letzte Wort, denn danach muss nicht mehr ausgepunktet werden.
        if (isLastLine && !isLastWord) {
          candidate = TruncatePipe.addFinalCharacter(candidate, endCharacter + '(XX)');
        }

        return TruncatePipe.getTextWidth(candidate, fontSize) < parentWidth;
      };

      // Füge ganze Wörter in die Zeile, solange noch Platz ist.
      while (wordIndex < words.length && lineStillFits()) {
        truncatedLine += ' ' + words[wordIndex];
        wordIndex++;
      }

      truncatedText += truncatedLine;
    }

    truncatedText = truncatedText.trim();

    return { truncatedText, isTruncated: wordIndex < words.length };
  }
}
